import { DataProvider, fetchUtils } from 'react-admin';
import { tokenService } from './services/tokenService';

const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const apiUrl = `${apiBaseUrl}/api`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const httpClient = (url: string, options: any = {}) => {
    const token = tokenService.getToken();
    if (!options.headers) {
        options.headers = new Headers({
            Accept: 'application/ld+json',
        });
    }
    if (token) {
        options.headers.set('Authorization', `Bearer ${token}`);
    }
    // Set Content-Type based on method
    if (options.method === 'POST') {
        options.headers.set('Content-Type', 'application/ld+json');
    } else if (options.method === 'PATCH') {
        options.headers.set('Content-Type', 'application/merge-patch+json');
    } else if (options.method === 'PUT') {
        options.headers.set('Content-Type', 'application/ld+json');
    }
    return fetchUtils.fetchJson(url, options);
};

// Convert IRI to ID (e.g., "/api/companies/1" => 1)
export const iriToId = (iri: string): number | string => {
    if (typeof iri === 'number') return iri;
    if (!iri || typeof iri !== 'string') return iri;
    const parts = iri.split('/');
    return parts[parts.length - 1];
};

// Convert ID to IRI (e.g., 1 => "/api/companies/1")
export const idToIri = (resource: string, id: number | string): string => {
    if (typeof id === 'string' && id.startsWith('/api/')) return id;
    return `/api/${resource}/${id}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const normalizeData = (data: any): any => {
    if (!data || typeof data !== 'object') return data;

    const normalized = { ...data };

    // Convert all IRI references to IDs
    for (const key in normalized) {
        const value = normalized[key];

        if (key === 'reminders' && Array.isArray(value)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            normalized[key] = value.map((reminder: any) => {
                if (!reminder || typeof reminder !== 'object') return reminder;
                const { daysBeforeDue, recipientType, emailTemplate } = reminder;
                const template = emailTemplate && typeof emailTemplate === 'object' ? emailTemplate['@id'] : emailTemplate;
                return { daysBeforeDue, recipientType, emailTemplate: template ? iriToId(template) : null };
            });
        }
        // Handle IRI strings
        else if (typeof value === 'string' && value.startsWith('/api/')) {
            normalized[key] = iriToId(value);
        }
        // Handle objects with @id (embedded resources OR bare IRI references).
        // Issue #275 Folge: das hier hat frueher jedes Objekt mit @id auf
        // nur die ID reduziert — und damit z.B. den eingebetteten catalog
        // ({ @id, @type, id, name }) auf "5" geschrumpft. Walters Kurskatalog
        // brauchte den eingebetteten Namen aber. Heuristik: nur flachklopfen,
        // wenn das Objekt AUSSER @id/@type/@context nichts traegt — sonst
        // bleibt es als Objekt erhalten und `id` wird zusaetzlich gesetzt
        // (kompatibel mit react-admin ReferenceField, das `.id` lesen kann).
        else if (value && typeof value === 'object' && !Array.isArray(value) && value['@id']) {
            const carries = Object.keys(value).some(
                k => k !== '@id' && k !== '@type' && k !== '@context',
            );
            if (carries) {
                normalized[key] = { ...value, id: iriToId(value['@id']) };
            } else {
                normalized[key] = iriToId(value['@id']);
            }
        }
        // Handle arrays — IMMER flachklopfen, weil react-admin
        // ReferenceArrayInput (z.B. userRoles in resources/users.tsx) ein
        // Array von IDs erwartet. Eingebettete Daten in Array-Refs werden
        // weiterhin auf reine IDs reduziert. Wer Detail-Daten aus einer
        // Array-Relation will, holt sie via dedizierter Query.
        else if (Array.isArray(value)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            normalized[key] = value.map((item: any) => {
                if (typeof item === 'string' && item.startsWith('/api/')) {
                    return iriToId(item);
                }
                if (item && typeof item === 'object' && item['@id']) {
                    return iriToId(item['@id']);
                }
                return item;
            });
        }
    }

    return normalized;
};

// Convert React Admin format to API Platform format
export const denormalizeData = (resource: string, data: any): any => {
    if (!data || typeof data !== 'object') return data;

    const denormalized: any = { ...data };

    // Remove read-only and computed fields that shouldn't be sent on update
    const readOnlyFields = [
        'id', 'uuid', '@id', '@type', '@context', 'roles',
        'createdAt', 'updatedAt', 'createdBy', 'companyId',
        'courseCount', 'requiredCourseCount', 'enrollmentCount',
        'userCount', 'lessonCount', 'attemptCount', 'progressPercentage',
    ];
    readOnlyFields.forEach(field => delete denormalized[field]);

    // Remove embedded collections (arrays of objects) — these are read-only relations
    for (const key in denormalized) {
        const value = denormalized[key];
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null && !Array.isArray(value[0])) {
            if (value[0]['@id'] || value[0]['id'] !== undefined) {
                delete denormalized[key];
            }
        }
    }

    // Convert reference IDs back to IRIs
    const referenceFields: Record<string, string> = {
        company: 'companies',
        product: 'products',
        user: 'users',
        role: 'roles',
        permission: 'permissions',
        license: 'licenses',
        userRole: 'roles',  // userRoles -> roles
        category: 'media_categories',
        folder: 'media_folders',
        course: 'courses',
        learningPath: 'learning_paths',
    };

    for (const key in denormalized) {
        // Single reference
        if (referenceFields[key] && denormalized[key] && !String(denormalized[key]).startsWith('/api/')) {
            denormalized[key] = idToIri(referenceFields[key], denormalized[key]);
        }
        // Array references (e.g., userRoles)
        else if (Array.isArray(denormalized[key]) && denormalized[key].length > 0) {
            // Special handling for specific array fields
            let targetResource: string | undefined = undefined;
            if (key === 'userRoles') {
                targetResource = 'roles';
            } else {
                // Default: remove trailing 's' and look up
                const singularKey = key.replace(/s$/, '');
                targetResource = referenceFields[singularKey];
            }

            if (targetResource) {
                denormalized[key] = denormalized[key].map((id: any) => {
                    if (typeof id === 'object' && id['@id']) return id['@id'];
                    if (String(id).startsWith('/api/')) return id;
                    return idToIri(targetResource!, id);
                });
            }
        }
    }

    // reminders[].emailTemplate is a nested ManyToOne; the loop above doesn't recurse, so a
    // bare id reaches the API as an integer and is rejected with 400 (IRI expected). Reduce to
    // the writable fields and convert the template id to an IRI (form moved from admin, #340).
    if (Array.isArray(denormalized.reminders)) {
        denormalized.reminders = denormalized.reminders.map((item: any) => {
            if (!item || typeof item !== 'object') return item;
            const days = item.daysBeforeDue;
            const tpl = item.emailTemplate;
            return {
                daysBeforeDue: days === undefined || days === null || days === '' ? days : Number(days),
                recipientType: item.recipientType,
                emailTemplate:
                    tpl === undefined || tpl === null || String(tpl).startsWith('/api/')
                        ? tpl
                        : idToIri('document_templates', tpl),
            };
        });
    }

    return denormalized;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const flattenFilterParams = (filter?: Record<string, any>): Record<string, string> => {
    const query: Record<string, string> = {};
    if (!filter) return query;

    Object.keys(filter).forEach(key => {
        const value = filter[key];
        if (value === undefined || value === null || value === '') return;

        if (Array.isArray(value)) {
            value.forEach((v, index) => {
                query[`${key}[${index}]`] = String(v);
            });
        } else if (typeof value === 'object') {
            Object.keys(value).forEach(subKey => {
                const subValue = value[subKey];
                if (subValue === undefined || subValue === null || subValue === '') return;
                query[`${key}.${subKey}`] = String(subValue);
            });
        } else {
            query[key] = String(value);
        }
    });

    return query;
};

const dataProvider: DataProvider = {
    getList: (resource, params) => {
        const page = params.pagination?.page || 1;
        const perPage = params.pagination?.perPage || 10;
        const field = params.sort?.field || 'id';
        const order = params.sort?.order || 'ASC';

        const query: Record<string, string> = {
            [`order[${field}]`]: order,
            page: page.toString(),
            itemsPerPage: perPage.toString(),
            ...flattenFilterParams(params.filter),
        };

        const url = `${apiUrl}/${resource}?${new URLSearchParams(query).toString()}`;

        return httpClient(url).then(({ json }) => {
            const members = json['hydra:member'] || json.member || [];
            return {
                data: members.map(normalizeData),
                total: json['hydra:totalItems'] || json.totalItems || 0,
            };
        });
    },

    getOne: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
            data: normalizeData(json),
        })),

    getMany: (resource, params) => {
        const query = params.ids.map((id: string | number) => `id[]=${id}`).join('&');
        const url = `${apiUrl}/${resource}?${query}`;
        return httpClient(url).then(({ json }) => {
            const members = json['hydra:member'] || json.member || [];
            return {
                data: members.map(normalizeData),
            };
        });
    },

    getManyReference: (resource, params) => {
        const page = params.pagination?.page || 1;
        const perPage = params.pagination?.perPage || 10;
        const order = params.sort?.order || 'ASC';

        const query: Record<string, string> = {
            [params.target]: String(params.id),
            'order[id]': order,
            page: page.toString(),
            itemsPerPage: perPage.toString(),
        };

        const url = `${apiUrl}/${resource}?${new URLSearchParams(query).toString()}`;

        return httpClient(url).then(({ json }) => {
            const members = json['hydra:member'] || json.member || [];
            return {
                data: members.map(normalizeData),
                total: json['hydra:totalItems'] || json.totalItems || 0,
            };
        });
    },

    create: (resource, params) => {
        const data = denormalizeData(resource, params.data);
        return httpClient(`${apiUrl}/${resource}`, {
            method: 'POST',
            body: JSON.stringify(data),
        }).then(({ json }) => ({
            data: normalizeData(json),
        }));
    },

    update: (resource, params) => {
        const data = denormalizeData(resource, params.data);
        return httpClient(`${apiUrl}/${resource}/${params.id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        }).then(({ json }) => ({
            data: normalizeData(json),
        }));
    },

    updateMany: (resource, params) => {
        const data = denormalizeData(resource, params.data);
        return Promise.all(
            params.ids.map(id =>
                httpClient(`${apiUrl}/${resource}/${id}`, {
                    method: 'PATCH',
                    body: JSON.stringify(data),
                })
            )
        ).then(responses => ({ data: responses.map(({ json }) => json.id) }));
    },

    delete: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}`, {
            method: 'DELETE',
        }).then(({ json }) => ({ data: normalizeData(json) })),

    deleteMany: (resource, params) => {
        return Promise.all(
            params.ids.map(id =>
                httpClient(`${apiUrl}/${resource}/${id}`, {
                    method: 'DELETE',
                })
            )
        ).then(responses => ({ data: params.ids }));
    },
};

export default dataProvider;
